import React, { useCallback } from 'react';
import { KatButton } from '@amzn/katal-react';
import { UseCase } from '../../services/AppConfigDeployer/AppConfigDeployerService';
import { VALID_WEBLABS, VALID_CAPS } from './processorPaymentMethodList';
import { rowKey, rowsToConfig } from './types';
import { useAppConfigEditor } from './useAppConfigEditor';
import JsonDiffView from './JsonDiffView';

interface Props {
    useCases: UseCase[];
    loadingUseCases: boolean;
    selectedUseCase: string;
    onUseCaseChange: (uc: string) => void;
    editor: ReturnType<typeof useAppConfigEditor>;
}

const ConfigEditorTab: React.FC<Props> = ({
    useCases, loadingUseCases, selectedUseCase, onUseCaseChange, editor,
}) => {
    const focusOnMount = useCallback((el: HTMLDivElement | null) => el?.focus(), []);

    const {
        originalRows, editedRows, configDescription, currentVersion, loadingConfig,
        selectedIndices, showWeblabModal, setShowWeblabModal, showCapModal, setShowCapModal,
        showDiff, setShowDiff, newProcessor, setNewProcessor, newPaymentMethod, setNewPaymentMethod,
        addEntryError, setAddEntryError, newEntryKeys, saveLoading, saveError, saveSuccess,
        searchTerm, setSearchTerm, filteredIndices, hasChanges, incompleteEntries,
        conflictError, setConflictError,
        loadConfig, toggleSelect, toggleSelectAll, applyWeblab, applyCap,
        deleteSelected, addEntry, handleSaveVersion,
    } = editor;

    return (
        <>
            <div className="tab-pane">
                {/* Use Case selector */}
                <div className="appconfig-selectors">
                    <div className="selector-group">
                        <label>Use Case</label>
                        <select
                            value={selectedUseCase}
                            onChange={(e: any) => { onUseCaseChange(e.target.value); setShowDiff(false); }}
                            className="selector-select"
                            disabled={loadingUseCases}
                        >
                            <option value="">{loadingUseCases ? 'Loading...' : 'Select a use case...'}</option>
                            {useCases.map((uc: UseCase) => <option key={uc.name} value={uc.name}>{uc.name}</option>)}
                        </select>
                    </div>
                </div>

                {!selectedUseCase && (
                    <div className="table-scroll-container">
                        <table className="config-table">
                            <thead>
                                <tr>
                                    <th className="col-checkbox"><input type="checkbox" disabled /></th>
                                    <th>Processor</th>
                                    <th>Payment Method</th>
                                    <th>Weblab</th>
                                    <th>Amount Cap</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td colSpan={5} className="empty-state">Select a use case to view and edit configuration</td></tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {selectedUseCase && loadingConfig && (
                    <div className="loading-overlay">
                        <div className="spinner" />
                        <span>Retrieving configurations</span>
                    </div>
                )}

                {selectedUseCase && !loadingConfig && (
                    <>
                        {/* Toolbar */}
                        <div className="config-toolbar">
                            <div className="toolbar-left">
                                <span className="version-badge">v{currentVersion}</span>
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Search processors..."
                                        value={searchTerm}
                                        onChange={(e: any) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="toolbar-right">
                                {selectedIndices.size > 0 && (
                                    <>
                                        <span className="selection-count">{selectedIndices.size} selected</span>
                                        <KatButton variant="secondary" label="Modify Weblab"
                                            onClick={() => setShowWeblabModal(true)} />
                                        <KatButton variant="secondary" label="Modify Amount Cap"
                                            onClick={() => setShowCapModal(true)} />
                                        <KatButton variant="danger" label="Delete Selected"
                                            onClick={deleteSelected} />
                                    </>
                                )}
                                <KatButton variant="primary" label="Diff View"
                                    onClick={() => setShowDiff(!showDiff)}
                                    disabled={!hasChanges} />
                                <KatButton variant="primary" label={saveLoading ? 'Saving...' : 'Save Version'}
                                    onClick={handleSaveVersion}
                                    disabled={saveLoading || !hasChanges} />
                                <KatButton variant="secondary" label={loadingConfig ? '↻' : 'Refresh'}
                                    onClick={() => loadConfig()}
                                    disabled={loadingConfig} />
                            </div>
                        </div>

                        {/* Add Entry Form */}
                        <div className="add-entry-form">
                            <input
                                type="text"
                                placeholder="Processor"
                                value={newProcessor}
                                onChange={(e: any) => { setNewProcessor(e.target.value); setAddEntryError(null); }}
                                className="add-entry-input"
                            />
                            <input
                                type="text"
                                placeholder="Payment Method"
                                value={newPaymentMethod}
                                onChange={(e: any) => { setNewPaymentMethod(e.target.value); setAddEntryError(null); }}
                                className="add-entry-input"
                            />
                            <KatButton variant="secondary" label="Add Entry" onClick={addEntry} />
                            {addEntryError && <span className="add-entry-error">{addEntryError}</span>}
                        </div>

                        {saveError && <div className="error-banner">{saveError}</div>}
                        {saveSuccess && <div className="success-banner">{saveSuccess}</div>}

                        {/* Diff View */}
                        {showDiff && (
                            <div className="diff-section">
                                <h3>Changes</h3>
                                <div className="json-diff-container">
                                    <JsonDiffView
                                        oldData={rowsToConfig(originalRows, configDescription)}
                                        newData={rowsToConfig(editedRows, configDescription)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Main Config Table */}
                        {!showDiff && (
                            <div className="table-scroll-container">
                            <table className="config-table">
                                <thead>
                                    <tr>
                                        <th className="col-checkbox">
                                            <input type="checkbox"
                                                checked={filteredIndices.length > 0 && selectedIndices.size === filteredIndices.length}
                                                onChange={toggleSelectAll} />
                                        </th>
                                        <th>Processor</th>
                                        <th>Payment Method</th>
                                        <th>Weblab</th>
                                        <th>Amount Cap</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredIndices.map((idx) => {
                                        const row = editedRows[idx];
                                        const key = rowKey(row.processor, row.paymentMethod);
                                        const isNew = newEntryKeys.has(key);
                                        const isIncomplete = incompleteEntries.has(key);
                                        return (
                                            <tr key={key}
                                                className={`${selectedIndices.has(idx) ? 'row-selected' : ''} ${isNew ? 'row-new' : ''} ${isIncomplete ? 'row-error' : ''}`}
                                                onClick={() => toggleSelect(idx)}
                                                style={{ cursor: 'pointer' }}>
                                                <td className="col-checkbox" onClick={(e: any) => e.stopPropagation()}>
                                                    <input type="checkbox"
                                                        checked={selectedIndices.has(idx)}
                                                        onChange={() => toggleSelect(idx)} />
                                                </td>
                                                <td className="col-processor">{row.processor}</td>
                                                <td>{row.paymentMethod}</td>
                                                <td>
                                                    {row.weblab ? (
                                                        <span className={`weblab-badge weblab-${row.weblab}`}>{row.weblab}</span>
                                                    ) : (
                                                        <span className="required-badge">Select Weblab</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {row.amountCap ? (
                                                        <span className={`cap-badge cap-${row.amountCap === 'NoCAP' ? 'nocap' : row.amountCap}`}>
                                                            {row.amountCap === 'NoCAP' ? 'No cap' : `$${row.amountCap}`}
                                                        </span>
                                                    ) : (
                                                        <span className="required-badge">Select Amount Cap</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filteredIndices.length === 0 && (
                                        <tr><td colSpan={5} className="empty-state">
                                            {editedRows.length === 0 ? 'No configuration data.' : 'No matching processors.'}
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Weblab Modal ── */}
            {showWeblabModal && (
                <div className="dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="weblab-modal-title"
                    tabIndex={-1} ref={focusOnMount}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') setShowWeblabModal(false); }}>
                    <div className="dialog">
                        <h3 id="weblab-modal-title">Select Weblab</h3>
                        <p>Apply to {selectedIndices.size} selected row(s)</p>
                        <div className="modal-options">
                            {VALID_WEBLABS.map(w => (
                                <button key={w} type="button" className="modal-option-btn" onClick={() => applyWeblab(w)}>{w}</button>
                            ))}
                        </div>
                        <div className="dialog-actions">
                            <KatButton variant="secondary" label="Cancel" onClick={() => setShowWeblabModal(false)} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Amount Cap Modal ── */}
            {showCapModal && (
                <div className="dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="cap-modal-title"
                    tabIndex={-1} ref={focusOnMount}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') setShowCapModal(false); }}>
                    <div className="dialog">
                        <h3 id="cap-modal-title">Select Amount Cap</h3>
                        <p>Apply to {selectedIndices.size} selected row(s)</p>
                        <div className="modal-options">
                            {VALID_CAPS.map(c => (
                                <button key={c} type="button" className="modal-option-btn" onClick={() => applyCap(c)}>
                                    {c === 'NoCAP' ? 'No Cap' : `$${c}`}
                                </button>
                            ))}
                        </div>
                        <div className="dialog-actions">
                            <KatButton variant="secondary" label="Cancel" onClick={() => setShowCapModal(false)} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Conflict Error Popup ── */}
            {conflictError && (
                <div className="dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="conflict-modal-title"
                    tabIndex={-1} ref={focusOnMount}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') setConflictError(null); }}>
                    <div className="dialog">
                        <h3 id="conflict-modal-title">Conflict Detected</h3>
                        <p>{conflictError}</p>
                        <div className="dialog-actions">
                            <KatButton variant="primary" label="OK" onClick={() => setConflictError(null)} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ConfigEditorTab;
